import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FiSearch, FiX, FiRefreshCw, FiXCircle, FiUserMinus, FiUsers, FiUserPlus } from "react-icons/fi";
import Can from "../Can";
import ConfirmModal from "../common/ConfirmModal";
import PublicLinkUrlBox from "./PublicLinkUrlBox";
import ExternalRecipientBadge, { isExternalRecipient } from "./ExternalRecipientBadge";
import "./ManageAssignedUsersModal.css";

// Shared "manage assigned employees" dialog (view/remove/add recipients,
// plus Phase 16's per-assignee public-link management) -- same business
// rules and the SAME component for Admin and every other authorized role
// (Developer's RoleFormAssignmentsPage.jsx included), all driven by the
// ONE useFormAssignments() hook. Only the surrounding page differs.
//
// Redesign: "Add Employees" now reads from `eligibleUsers` -- a backend-
// searched, server-paginated directory (GET /form-assignments/:id/
// eligible-users) that shows every ACTIVE user in the system (never
// narrowed by the assigner's own create-scope or by the assignment's
// project), with already-active recipients of THIS assignment already
// excluded server-side. See useFormAssignments.js's own header comments
// on eligibleUsers/searchEligibleUsers/loadMoreEligibleUsers for the full
// audit trail of what this replaces and why.
//
// Section 9 fix: a null `users.name` in the DB used to render literally as
// "null (username)" here (and in the Add Employees list). getUserDisplayName()
// below is the single fallback chain both lists use instead.
function getUserDisplayName(user) {
  if (!user) return "Unknown User";
  return (
    user.user_name ||
    user.full_name ||
    user.name ||
    user.employee_name ||
    user.display_name ||
    user.user_email ||
    user.email ||
    user.username ||
    "Unknown User"
  );
}

// Phase 16 (Public Form Links), §19: derives a display status purely from
// the fields listUsers() already returns (has_public_link/
// public_token_expires_at/public_token_revoked_at) — never the raw
// token/hash, which the backend never sends here at all.
function publicLinkStatus(u) {
  if (!u.has_public_link) return "none";
  if (u.public_token_revoked_at) return "revoked";
  if (u.public_token_expires_at && new Date(u.public_token_expires_at) < new Date()) return "expired";
  return "active";
}

const LINK_STATUS_BADGE = {
  none: { className: "mau-badge-neutral", label: "No Link" },
  active: { className: "mau-badge-active", label: "Active" },
  expired: { className: "mau-badge-warn", label: "Expired" },
  revoked: { className: "mau-badge-revoked", label: "Revoked" },
};

const SUBMISSION_BADGE_VARIANT = {
  ASSIGNED: "mau-status-neutral",
  IN_PROGRESS: "mau-status-info",
  SUBMITTED: "mau-status-success",
  COMPLETED: "mau-status-success",
  CANCELLED: "mau-status-muted",
};

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// One assignee row: name/username, submission status, public-link status,
// and the (visually distinct — §11) compact icon+label action group.
// Regenerate/Revoke each carry their OWN confirmation copy (§12) so
// "invalidate the link" is never confused with "remove this person from
// the assignment" (§7/§22).
function AssigneeRow({ u, assignmentId, onRequestRemove, linkBusy, onGenerateLink, onRegenerateLink, onRevokeLink, generatedLinkUrl }) {
  const linkStatus = publicLinkStatus(u);
  const badge = LINK_STATUS_BADGE[linkStatus];
  const displayName = getUserDisplayName(u);
  const showUsername = u.username && u.username !== displayName;
  // External recipient (no account): show its email under a label-only name
  // and the shared "External" badge; registered rows render exactly as before.
  const external = isExternalRecipient(u);
  const externalEmail = external ? (u.recipient_email || u.user_email) : null;

  return (
    <div className="mau-row">
      <div className="mau-row-top">
        <div className="mau-row-identity">
          <div className="mau-avatar">{initialsOf(displayName)}</div>
          <div className="mau-identity-text">
            <div className="mau-employee-name">
              {displayName}
              <ExternalRecipientBadge external={external} />
            </div>
            {showUsername && <div className="mau-employee-username">@{u.username}</div>}
            {externalEmail && externalEmail !== displayName && <div className="mau-employee-username">{externalEmail}</div>}
          </div>
        </div>

        <div className="mau-row-meta">
          <div className="mau-meta-field">
            <span className="mau-meta-label">Submission</span>
            <span className={`mau-status-pill ${SUBMISSION_BADGE_VARIANT[u.status] || "mau-status-neutral"}`}>{u.status}</span>
            {u.submission_count > 0 && <span className="mau-meta-sub">{u.submission_count} submission(s)</span>}
          </div>
          <div className="mau-meta-field">
            <span className="mau-meta-label">Public Link</span>
            <span className={`mau-link-badge ${badge.className}`}>
              <span className="mau-link-dot" /> {badge.label}
            </span>
          </div>
        </div>
      </div>

      <div className="mau-row-actions">
        {u.status !== "CANCELLED" && onGenerateLink && (
          <>
            <Can moduleKey="form_assignments" actionKey="assign_users">
              {linkStatus === "none" && (
                <button type="button" className="mau-btn mau-btn-generate" disabled={linkBusy} onClick={() => onGenerateLink(assignmentId, u.id)}>
                  <FiUserPlus aria-hidden="true" /> {linkBusy ? "Working…" : "Generate & Copy"}
                </button>
              )}
              {linkStatus === "active" && (
                <button
                  type="button"
                  className="mau-btn mau-btn-regenerate"
                  disabled={linkBusy}
                  onClick={() =>
                    onRegenerateLink(assignmentId, u.id, {
                      confirmMessage: "Regenerating this link will immediately invalidate the existing public link.\n\nContinue?",
                    })
                  }
                >
                  <FiRefreshCw aria-hidden="true" /> {linkBusy ? "Working…" : "Regenerate & Copy"}
                </button>
              )}
              {(linkStatus === "expired" || linkStatus === "revoked") && (
                <button type="button" className="mau-btn mau-btn-generate" disabled={linkBusy} onClick={() => onRegenerateLink(assignmentId, u.id, {})}>
                  <FiUserPlus aria-hidden="true" /> {linkBusy ? "Working…" : "Generate New Link"}
                </button>
              )}
            </Can>
            {linkStatus === "active" && (
              // Revoke hits DELETE .../public-link, gated server-side on
              // `remove_users` (not `assign_users`) -- see routes/formAssignmentRoutes.js.
              <Can moduleKey="form_assignments" actionKey="remove_users">
                <button
                  type="button"
                  className="mau-btn mau-btn-revoke"
                  disabled={linkBusy}
                  onClick={() =>
                    onRevokeLink(assignmentId, u.id, {
                      confirmMessage: "This will disable public-link access for this user. Their assignment and submission will not be deleted.",
                    })
                  }
                >
                  <FiXCircle aria-hidden="true" /> Revoke
                </button>
              </Can>
            )}
          </>
        )}
        {u.status !== "CANCELLED" && (
          <Can moduleKey="form_assignments" actionKey="remove_users">
            <button type="button" className="mau-btn mau-btn-remove" onClick={() => onRequestRemove(u.id)}>
              <FiUserMinus aria-hidden="true" /> Remove
            </button>
          </Can>
        )}
      </div>
      {/* Full absolute URL a Generate/Regenerate just returned this session
          -- see useFormAssignments.js's `generatedLinks`. Nothing to show
          after a page refresh (only the hash is persisted server-side). */}
      {linkStatus === "active" && generatedLinkUrl && (
        <div className="mau-row-link">
          <PublicLinkUrlBox url={generatedLinkUrl} />
        </div>
      )}
    </div>
  );
}

// One selectable "Add Employees" row -- the whole row toggles the
// checkbox (§9), not just the checkbox hit-target itself.
function EligibleUserRow({ u, checked, onToggle }) {
  const displayName = getUserDisplayName(u);
  const email = u.email || u.user_email;
  const subtitle = u.designation_name || u.department_name;
  return (
    <label className={`mau-add-item${checked ? " mau-add-item-checked" : ""}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className="mau-add-item-avatar">{initialsOf(displayName)}</span>
      <span className="mau-add-item-text">
        <span className="mau-add-item-name">{displayName}</span>
        {email && <span className="mau-add-item-email">{email}</span>}
        {subtitle && <span className="mau-add-item-role">{subtitle}</span>}
      </span>
    </label>
  );
}

export default function ManageAssignedUsersModal({
  target, onClose, loading, assignedUsers,
  eligibleUsers, eligibleUsersTotal, eligibleUsersLoading, eligibleUsersPageSize = 50,
  eligibleUsersSearch, onSearchEligibleUsers, onLoadMoreEligibleUsers,
  addUserIds, onToggleAddUser, onAddUsers,
  removeUserTarget, onRequestRemove, onCancelRemove, onConfirmRemove, removing,
  linkBusyId, onGenerateLink, onRegenerateLink, onRevokeLink, generatedLinks,
}) {
  // Local, immediate value the search box shows -- the hook's own
  // eligibleUsersSearch only updates (and triggers the backend fetch)
  // after the debounce below, so typing never feels laggy even though the
  // actual search is server-side (§6/§8).
  const [searchInput, setSearchInput] = useState(eligibleUsersSearch || "");

  useEffect(() => {
    setSearchInput(eligibleUsersSearch || "");
    // Only reset when a DIFFERENT assignment's modal opens -- never
    // fight the user's own typing on every hook-driven search update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  useEffect(() => {
    if (!target) return undefined;
    const handle = setTimeout(() => {
      if (searchInput !== eligibleUsersSearch) onSearchEligibleUsers(searchInput);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, target?.id]);

  const hasMore = eligibleUsers.length < eligibleUsersTotal;
  const selectedCount = addUserIds.length;

  return (
    <>
      <Modal show={!!target} onHide={onClose} centered size="lg" dialogClassName="mau-modal-dialog" className="unit-modal mau-modal">
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Manage Assigned Employees — {target?.assignment_title}</Modal.Title>
            <p className="mau-subtitle">Manage recipients and secure public access links.</p>
          </div>
        </Modal.Header>
        <Modal.Body className="mau-body">
          {loading ? (
            <div className="unit-loader"><Spinner animation="border" /></div>
          ) : (
            <>
              <div className="mau-section-title">
                <FiUsers aria-hidden="true" /> Assigned Employees ({assignedUsers.length})
              </div>
              <div className="mau-employee-list">
                {assignedUsers.length === 0 && <div className="text-muted small">No employees assigned yet.</div>}
                {assignedUsers.map((u) => (
                  <AssigneeRow
                    key={u.id}
                    u={u}
                    assignmentId={target?.id}
                    onRequestRemove={onRequestRemove}
                    linkBusy={linkBusyId === u.id}
                    onGenerateLink={onGenerateLink}
                    onRegenerateLink={onRegenerateLink}
                    onRevokeLink={onRevokeLink}
                    generatedLinkUrl={generatedLinks?.[u.id]}
                  />
                ))}
              </div>

              <Can moduleKey="form_assignments" actionKey="assign_users">
                <div className="mau-section-title mau-section-title-add">Add Employees</div>

                <div className="mau-add-search-wrap">
                  <FiSearch className="mau-add-search-icon" aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Search by name, email or username..."
                    aria-label="Search employees to add"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="mau-add-search"
                  />
                  {eligibleUsersLoading && <Spinner animation="border" size="sm" className="mau-add-search-spinner" />}
                  {!eligibleUsersLoading && searchInput && (
                    <button
                      type="button"
                      className="mau-add-search-clear"
                      aria-label="Clear search"
                      onClick={() => setSearchInput("")}
                    >
                      <FiX aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="mau-add-list">
                  {!eligibleUsersLoading && eligibleUsers.length === 0 && (
                    <div className="text-muted small p-2">
                      {searchInput
                        ? "No employees found."
                        : eligibleUsersTotal === 0
                          ? "All active employees are already assigned."
                          : "No employees found."}
                    </div>
                  )}
                  {eligibleUsers.map((u) => (
                    <EligibleUserRow
                      key={u.id}
                      u={u}
                      checked={addUserIds.includes(u.id)}
                      onToggle={() => onToggleAddUser(u.id)}
                    />
                  ))}
                  {eligibleUsersLoading && eligibleUsers.length === 0 && (
                    <div className="mau-add-list-loading"><Spinner animation="border" size="sm" /> Loading employees…</div>
                  )}
                  {hasMore && (
                    <button type="button" className="mau-load-more" onClick={onLoadMoreEligibleUsers} disabled={eligibleUsersLoading}>
                      {eligibleUsersLoading ? "Loading…" : `Load more (${eligibleUsers.length} of ${eligibleUsersTotal})`}
                    </button>
                  )}
                </div>

                <div className="mau-add-footer">
                  <span className="mau-add-selected-count">
                    {selectedCount === 0 ? "0 employees selected" : `${selectedCount} employee${selectedCount === 1 ? "" : "s"} selected`}
                  </span>
                  <Button className="mau-btn mau-btn-add-selected" size="sm" disabled={selectedCount === 0} onClick={onAddUsers}>
                    <FiUserPlus aria-hidden="true" />
                    {selectedCount === 0 ? "Add Selected Employees" : `Add ${selectedCount} Employee${selectedCount === 1 ? "" : "s"}`}
                  </Button>
                </div>
              </Can>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={onClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!removeUserTarget}
        onHide={onCancelRemove}
        onConfirm={onConfirmRemove}
        busy={removing}
        title="Remove User"
        message="Remove this user from the assignment? Any already-submitted response is preserved."
        confirmLabel="Remove"
      />
    </>
  );
}
